import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Psychology,
  Speed,
  Warning,
  Insights,
  CompareArrows,
  Timer,
} from '@mui/icons-material';
import { Insight, CORRELATION_STRENGTH_COLORS } from '../../types/insights';

interface InsightCardProps {
  insight: Insight;
  categoryColor: string;
  onExploreWithAI: (prompt: string, precomputedExplanation?: string) => void;
}

// Global cooldown state to throttle across all cards
let globalCooldownUntil = 0;
const COOLDOWN_SECONDS = 5;

const InsightCard: React.FC<InsightCardProps> = ({
  insight,
  categoryColor,
  onExploreWithAI,
}) => {
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const handleExploreClick = () => {
    const now = Date.now();
    
    // Check global cooldown
    if (now < globalCooldownUntil) {
      const remaining = Math.ceil((globalCooldownUntil - now) / 1000);
      setCooldownRemaining(remaining);
      setIsOnCooldown(true);
      
      // Start countdown timer
      const timer = setInterval(() => {
        const newRemaining = Math.ceil((globalCooldownUntil - Date.now()) / 1000);
        if (newRemaining <= 0) {
          setIsOnCooldown(false);
          setCooldownRemaining(0);
          clearInterval(timer);
        } else {
          setCooldownRemaining(newRemaining);
        }
      }, 1000);
      
      return;
    }
    
    // Set global cooldown
    globalCooldownUntil = now + (COOLDOWN_SECONDS * 1000);
    setIsOnCooldown(true);
    setCooldownRemaining(COOLDOWN_SECONDS);
    
    // Call the actual handler with precomputed explanation if available
    onExploreWithAI(insight.copilot_prompt, insight.precomputed_explanation);
    
    // Start countdown timer
    const timer = setInterval(() => {
      const remaining = Math.ceil((globalCooldownUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setIsOnCooldown(false);
        setCooldownRemaining(0);
        clearInterval(timer);
      } else {
        setCooldownRemaining(remaining);
      }
    }, 1000);
  };

  const getStrengthColor = (strength: string): string => {
    return CORRELATION_STRENGTH_COLORS[strength] || '#A0AEC0';
  };

  const getStrengthLabel = (strength: string): string => {
    switch (strength) {
      case 'strong':
        return 'Strong Correlation';
      case 'moderate':
        return 'Moderate Correlation';
      case 'weak':
        return 'Weak Correlation';
      case 'critical':
        return 'Critical';
      case 'baseline':
        return 'Baseline';
      default:
        return strength;
    }
  };

  const getTrendIcon = () => {
    // Analyze the insight data to determine trend direction
    const finding = insight.finding.toLowerCase();
    if (finding.includes('improving') || finding.includes('better') || finding.includes('faster')) {
      return <TrendingUp sx={{ color: '#48BB78', fontSize: 20 }} />;
    }
    if (finding.includes('declining') || finding.includes('slower') || finding.includes('behind')) {
      return <TrendingDown sx={{ color: '#F56565', fontSize: 20 }} />;
    }
    return <TrendingFlat sx={{ color: '#A0AEC0', fontSize: 20 }} />;
  };

  const getCategoryIcon = () => {
    const id = insight.id.toLowerCase();
    if (id.includes('velocity') || id.includes('speed') || id.includes('first_shot')) {
      return <Speed sx={{ color: categoryColor, fontSize: 24 }} />;
    }
    if (id.includes('overtime') || id.includes('risk') || id.includes('delay') || id.includes('drift')) {
      return <Warning sx={{ color: categoryColor, fontSize: 24 }} />;
    }
    if (id.includes('correlation') || id.includes('fatigue') || id.includes('pattern')) {
      return <Insights sx={{ color: categoryColor, fontSize: 24 }} />;
    }
    if (id.includes('comparative') || id.includes('trend') || id.includes('episode')) {
      return <CompareArrows sx={{ color: categoryColor, fontSize: 24 }} />;
    }
    return <Insights sx={{ color: categoryColor, fontSize: 24 }} />;
  };

  return (
    <Card
      sx={{
        backgroundColor: '#2D3748',
        borderRadius: 2,
        border: '1px solid #4A5568',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: categoryColor,
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3)`,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
        {/* Header */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            {getCategoryIcon()}
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="white"
              sx={{ lineHeight: 1.3 }}
            >
              {insight.title}
            </Typography>
          </Box>
          {getTrendIcon()}
        </Box>

        {/* Key Finding */}
        <Box
          sx={{
            backgroundColor: 'rgba(66, 153, 225, 0.1)',
            borderRadius: 1,
            p: 1.5,
            mb: 2,
            borderLeft: `3px solid ${categoryColor}`,
          }}
        >
          <Typography
            variant="body2"
            color="white"
            fontWeight={500}
            sx={{ lineHeight: 1.5 }}
          >
            {insight.finding}
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, flexGrow: 1, lineHeight: 1.6 }}
        >
          {insight.description}
        </Typography>

        {/* Correlation Strength Badge */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Tooltip title="Indicates how strongly these factors are related">
            <Chip
              label={getStrengthLabel(insight.correlation_strength)}
              size="small"
              sx={{
                backgroundColor: `${getStrengthColor(insight.correlation_strength)}20`,
                color: getStrengthColor(insight.correlation_strength),
                fontWeight: 500,
                fontSize: '0.75rem',
                borderRadius: 1,
              }}
            />
          </Tooltip>
          
          {insight.variables_involved && insight.variables_involved.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {insight.variables_involved.length} variables
            </Typography>
          )}
        </Box>

        {/* Explore with AI Button */}
        <Button
          variant="outlined"
          size="small"
          disabled={isOnCooldown}
          startIcon={isOnCooldown ? <Timer /> : <Psychology />}
          onClick={handleExploreClick}
          sx={{
            borderColor: isOnCooldown ? '#4A5568' : categoryColor,
            color: isOnCooldown ? '#718096' : categoryColor,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: isOnCooldown ? '#4A5568' : categoryColor,
              backgroundColor: isOnCooldown ? 'transparent' : `${categoryColor}15`,
            },
            '&.Mui-disabled': {
              borderColor: '#4A5568',
              color: '#718096',
            },
          }}
        >
          {isOnCooldown ? `Wait ${cooldownRemaining}s` : 'Explore with AI'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default InsightCard;
